import _ from 'underscore';
import ko from 'knockout';
import Backbone from 'backbone';
import config from '@bingads-webui-component-legacy/config/index';
import pgrid from '@bingads-webui-component-legacy/github/modules/projection-grid/module/projection-grid';
import paginationPlugin from '@bingads-webui-component-legacy/bingads-grid/plugin/pagination';
import { inlineErrorPlugin } from '@bingads-webui/projection-grid-inline-error';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import template from '@bingads-webui-campaign-legacy/budget/template/base-bid-grid.pug';
import ViewModel from '@bingads-webui-campaign-legacy/budget/viewmodel/base-bid-grid';
import { validate as validateData } from '@bingads-webui-campaign-legacy/budget/shared/bidValidate';
import { countNumberOfDigits } from '@bingads-webui-campaign-legacy/budget/shared/countNumberOfDigits';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const bidRange = config.get('KeywordBidRange');

export class BaseAdGroupBidGrid extends Backbone.View {
  initialize({
    gridColumns = [],
    gridName,
    defaultBid = bidRange.FloorValue,
    newI18n,
    activityLogger,
    girdAriaLabel,
  } = {}) {
    this.gridColumns = gridColumns;
    this.defaultBid = defaultBid;
    this.gridName = gridName;
    this.activityLogger = activityLogger;
    this.isBidChanged = false;
    this.newI18n = newI18n;
    this.viewModel = new ViewModel();
    this.girdAriaLabel = girdAriaLabel;
  }

  render() {
    this.$el.append(template({i18n}));
    ko.applyBindings(this.viewModel, this.el);

    this.grid = pgrid.factory({ vnext: true })
      .use(paginationPlugin)
      .use(inlineErrorPlugin({
        errorIconClasses: ['grid-error', 'spritedimage'],
      }))
      .create({
        name: this.gridName,
        tableClasses: ['table', 'table-bordered'],
        tableAttributes: { 'aria-label': this.girdAriaLabel },
        dataSource: {
          type: 'memory',
          data: [],
          primaryKey: 'Id',
        },
        rows: {
          bodyRows: [{
            type: 'data-rows',
            classes: ['adgroup-bid-row'],
          }],
        },
        columns: this.gridColumns,
        events: {
          'change .input-bid': (event) => {
            if (this.activityLogger) {
              this.activityLogger.signal(`adgroup bid changed: ${event.target && event.target.value}`);
            }
            this.bidUpdated(event);
          },
        },
      });

    this.$('.grid').html(this.grid.gridView.render().el);

    return this;
  }

  bidUpdated() {
    throw new Error('Should implement bidUpdated function');
  }

  patchError(newError) {
    const { errors } = this.grid.gridView.get('inlineError');

    this.grid.inlineError.setErrors(_.extend({}, errors, newError));
  }

  validateBid(item) {
    const newBidFloat = decimal.fromLocalToFloat(item.Bid);

    if (!decimal.isValid(item.Bid, true)) {
      this.patchError({
        [item.Id]: [i18n.get('Validation_BidFormatInvalid')],
      });

      return {
        isValid: false,
        bidHintKey: 'Validation_BidFormatInvalid',
      };
    } else if (newBidFloat < bidRange.FloorValue || newBidFloat > bidRange.CeilingValue) {
      const message = i18n.get('Budget_TrafficEstimation_BidLimitationError')
        .replace('{0}', bidRange.FloorValue)
        .replace('{1}', bidRange.CeilingValue);

      this.patchError({
        [item.Id]: [message],
      });

      return {
        isValid: false,
        bidHintKey: 'Budget_TrafficEstimation_BidLimitationError',
      };
    } else if (countNumberOfDigits(newBidFloat) > 2) {
      this.patchError({
        [item.Id]: [i18n.get('Validation_Number_Max_Length')],
      });

      return {
        isValid: false,
        bidHintKey: 'Validation_Number_Max_Length',
      };
    }

    this.patchError({
      [item.Id]: [],
    });

    return {
      isValid: true,
    };
  }

  resetMesages() {
    this.viewModel.errorMessage('');
    this.viewModel.infoMessage('');
    this.viewModel.hasUnsupportedLocations(false);
    this.viewModel.showSearchBidValidationError(false);
  }

  update({ adGroups, campaignId, budget, languages }) {
    this.resetMesages();

    if (_.isArray(adGroups)) {
      this.adGroups = adGroups;
      this.refreshGrid(adGroups);
    }

    if (!_.isUndefined(campaignId)) {
      this.campaignId = campaignId;
    }

    if (!_.isUndefined(languages)) {
      this.languages = languages;
    }

    // Always validate bid when switching from step 3 to step 4
    const isAllBidValid = _.reduce(this.adGroups,  (memo, adGroup) => {
      return this.validateBid(adGroup).isValid && memo;
    }, true);

    if (!this.viewModel.showSearchBidValidationError()) {
      this.viewModel.showSearchBidValidationError(true);
    }

    if (!_.isUndefined(budget)) {
      this.updateBudget(budget, isAllBidValid);
    }
  }

  updateBudget() {
    //do nothing
  }

  refreshGrid(data) {
    this.grid.gridView.patch({
      dataSource: {
        data: _.map(data, (item) => {
          const defaultBid = item.suggestedAvgCpc ? Math.max(item.suggestedAvgCpc, this.defaultBid) : this.defaultBid;

          return _.defaults(item, {
            Bid: decimal.fromFloatToString(defaultBid),
          });
        }),
      },
    });
  }

  getAdGroups() {
    return _.map(this.grid.gridView.get('dataSource').data, (adGroup) => {
      return {
        Campaign: {
          Id: this.campaignId,
        },
        Id: adGroup.Id,
        SearchBid: decimal.fromLocalToFloat(adGroup.Bid),
        isNKW: adGroup.isNKW || false,
        suggestedBid: adGroup.suggestedBid,
        viewedSuggestion: adGroup.viewedSuggestion || false,
        suggestedBidRequestId: adGroup.suggestedBidRequestId,
      };
    }, this);
  }

  validate() {
    return validateData(this.grid.gridView.get('dataSource').data);
  }

  enough() {
    return true;
  }

  remove(...args) {
    this.grid.gridView.remove();
    this.grid.pagination.remove();

    super.remove(...args);
  }
}
